import { getFeatureFlagValue } from '@citrite/feature-flags';
import { css, Global } from '@emotion/react';
import { sizes } from '@sharefiledev/antd-config';
import { Button, infoMessageModal, notifyError } from '@sharefiledev/flow-web';
import { useMutation } from '@tanstack/react-query';
import { ButtonProps, Spin } from 'antd';
import { useSignerPage } from '../../Common/Hooks/useSignerPage';
import { SignInSource } from '../../constants';
import { useShareFileUserContext } from '../../Contexts/ShareFileUserContext';
import { ESignatureClient } from '../../data/eSignatureClient';
import { DashboardSignButtonTypes } from '../../data/rsTypes';
import { FeatureFlag } from '../../FeatureFlags';
import { SignerPage } from '../../SignerPage';
import { t } from '../../util';

export interface DashboardSignButtonProps extends ButtonProps {
	signerId: string;
	buttonType: string;
	style?: React.CSSProperties;
}

export const DashboardSignButton: React.FC<DashboardSignButtonProps> = props => {
	const {
		isSignerPageDrawerOpen,
		setIsSignerPageDrawerOpen,
		isLoading,
		searchParams,
		startSignFromDashboard,
	} = useSignerPage(props.signerId);

	const { sfUser } = useShareFileUserContext();

	const sendLinkMutation = useMutation(
		(signerId: string) => {
			return ESignatureClient.remindSigner(signerId);
		},
		{
			onSuccess: () => {
				infoMessageModal({
					title: t('sendSigningLink.sentLinkInfoBanner'),
					message: (
						<>
							<p>
								{t('sendSigningLink.signingLinkInfoMessage1', {
									email: <strong>{sfUser.Email}</strong>,
								})}
							</p>
							<p>{t('sendSigningLink.signingLinkInfoMessage2')}</p>
						</>
					),
					confirmButtonText: t('sendSigningLink.gotIt'),
				});
			},
			onError: () => {
				notifyError(t('genericError'));
			},
		}
	);

	const handleSigning = () => {
		if (getFeatureFlagValue(FeatureFlag.EnableSignFromDashboard)) {
			startSignFromDashboard();
		} else {
			sendLinkMutation.mutate(props.signerId);
		}
	};

	return (
		<>
			<Global
				styles={css`
					.dashboard-sign-btn-secondary > div {
						line-height: ${sizes.XS} !important;
					}
				`}
			/>
			{isSignerPageDrawerOpen ? (
				<SignerPage
					isSignFromDashboardFlow
					signedInUserId={props.signerId}
					signedInUserSearchParams={searchParams}
					isSignerPageDrawerOpen={isSignerPageDrawerOpen}
					setIsSignerPageDrawerOpen={setIsSignerPageDrawerOpen}
					signInSource={SignInSource.EmployeePortal}
				/>
			) : isLoading ? (
				<Spin data-testid="page-spinner" style={{ width: sizes.XL }} />
			) : (
				<>
					{props.buttonType === DashboardSignButtonTypes.BabyPrimary ? (
						<Button.BabyPrimary
							onClick={handleSigning}
							style={props.style}
							data-testid={'dashboard-sign-button-baby-primary'}
						>
							{t('signButtonLabel')}
						</Button.BabyPrimary>
					) : (
						<Button.Secondary
							onClick={handleSigning}
							style={props.style}
							className="dashboard-sign-btn-secondary"
							data-testid={'dashboard-sign-button-secondary'}
						>
							{t('signButtonLabel')}
						</Button.Secondary>
					)}
				</>
			)}
		</>
	);
};
